import React, { useState } from 'react';
import { Offcanvas, Button, Card, ListGroup, Badge, Alert } from 'react-bootstrap';
import { 
  QuestionCircle, 
  Lightbulb, 
  Book, 
  ChevronLeft, 
  ChevronRight,
  InfoCircle,
  CheckCircle,
  ExclamationTriangle,
  ArrowRight,
  Envelope,
  Cloud
} from 'react-bootstrap-icons';

interface HelpContent {
  title: string;
  description: string;
  strategies?: Array<{
    title: string;
    description: string;
    benefits: string[];
    example?: string;
  }>;
  tips?: string[];
  warnings?: string[];
  examples?: Array<{
    title: string;
    description: string;
    code?: string;
  }>;
}

interface HelpSidebarProps {
  pageKey: string;
  isOpen: boolean;
  onToggle: () => void;
  selectedEmailPipe?: string;
}

const HelpSidebar: React.FC<HelpSidebarProps> = ({ pageKey, isOpen, onToggle, selectedEmailPipe }) => {
  const [activeSection, setActiveSection] = useState<string>('overview');

  // Help content for different pages
  const helpContent: Record<string, HelpContent> = {
    'cross-identity': {
      title: 'Cross-Identity Campaign Distribution',
      description: 'Distribute your campaigns across multiple email identities to maximize deliverability and avoid spam filters.',
      strategies: [
        {
          title: 'Domain Diversification',
          description: 'Use multiple domains to spread your sending load',
          benefits: ['Reduces spam flags', 'Maintains sender reputation', 'Increases sending capacity'],
          example: 'primary@domain.com (40%), newsletter@domain.com (30%), updates@domain.com (30%)'
        },
        {
          title: 'Account Diversification',
          description: 'Distribute across different email accounts',
          benefits: ['Avoids rate limits', 'Risk mitigation', 'Performance optimization'],
          example: 'admin@domain.com (25%), support@domain.com (25%), sales@domain.com (25%), info@domain.com (25%)'
        },
        {
          title: 'Geographic Distribution',
          description: 'Use region-specific identities for targeted campaigns',
          benefits: ['Better deliverability', 'Localized content', 'Compliance adherence'],
          example: 'us@domain.com (US), eu@domain.com (EU), asia@domain.com (Asia)'
        }
      ],
      tips: [
        'Start with 2-3 identities and gradually add more',
        'Monitor performance metrics for each identity',
        'Use A/B testing to optimize distribution ratios',
        'Keep consistent branding across all identities',
        'Rotate identities regularly to maintain freshness'
      ],
      warnings: [
        'Don\'t use too many identities at once - start small',
        'Ensure all identities are properly verified',
        'Monitor bounce rates and complaint rates closely',
        'Avoid sending identical content from all identities'
      ],
      examples: [
        {
          title: 'E-commerce Flash Sale',
          description: 'Distribute a flash sale campaign across multiple identities',
          code: 'marketing@store.com (40%) + sales@store.com (30%) + deals@store.com (20%) + offers@store.com (10%)'
        },
        {
          title: 'SaaS Feature Announcement',
          description: 'Announce new features using cross-identity distribution',
          code: 'product@company.com (50%) + updates@company.com (30%) + news@company.com (20%)'
        }
      ]
    },
    'rotation': {
      title: 'Account Rotation Scheduling',
      description: 'Automate email account rotation to distribute sending load, manage rate limits, and optimize deliverability across multiple email accounts.',
      strategies: [
        {
          title: 'Round Robin Rotation',
          description: 'Cycles through email accounts in a fixed, sequential order. Each email account is used equally before moving to the next.',
          benefits: [
            'Simple and predictable rotation pattern',
            'Equal load distribution across all email accounts',
            'Easy to implement and monitor',
            'Fair usage for all email accounts'
          ],
          example: 'Email Account A (support@company.com) sends → Email Account B (sales@company.com) sends → Email Account C (info@company.com) sends → Email Account A (repeat). Perfect for evenly distributing campaigns across multiple email accounts.'
        },
        {
          title: 'Time-Based Rotation',
          description: 'Switches email accounts at specific time intervals (e.g., every 2 hours, every 6 hours, daily). Rotation occurs automatically based on your schedule.',
          benefits: [
            'Automatic rotation without manual intervention',
            'Optimizes for time zones and business hours',
            'Prevents single email account overload',
            'Maintains consistent sending patterns'
          ],
          example: 'Set to rotate every 2 hours: 9:00 AM → Email Account A (marketing@company.com), 11:00 AM → Email Account B (newsletter@company.com), 1:00 PM → Email Account C (updates@company.com), 3:00 PM → Email Account A (repeat). Ideal for continuous sending throughout the day.'
        },
        {
          title: 'Volume-Based Rotation',
          description: 'Automatically switches email accounts when a volume threshold is reached (e.g., after sending 500 emails). Prevents hitting rate limits on a single email account.',
          benefits: [
            'Prevents rate limit violations',
            'Automatically distributes high-volume campaigns',
            'Adapts to sending volume automatically',
            'Maintains email account health by spreading load'
          ],
          example: 'Email Account A (campaign1@company.com) sends 500 emails → Automatically switches to Email Account B (campaign2@company.com) → Email Account B sends 500 emails → Switches to Email Account C (campaign3@company.com). Perfect for large campaigns that exceed single email account limits.'
        },
        {
          title: 'Health-Based Rotation',
          description: 'Switches email accounts based on health metrics (bounce rate, complaint rate, delivery rate). Automatically avoids email accounts with poor performance.',
          benefits: [
            'Protects sender reputation',
            'Automatically routes away from problematic email accounts',
            'Maintains high deliverability rates',
            'Self-healing rotation system'
          ],
          example: 'Email Account A (primary@company.com) has bounce rate > 5% → Automatically switches to Email Account B (backup@company.com) → Monitors Email Account A health → Returns to Email Account A when health improves. Ideal for maintaining optimal deliverability.'
        }
      ],
      tips: [
        'Start with Round Robin for simple, predictable email account rotation',
        'Use Time-Based for continuous sending throughout the day',
        'Use Volume-Based for large campaigns that exceed single email account limits',
        'Use Health-Based to automatically protect sender reputation',
        'Monitor each email account\'s performance metrics after rotation',
        'Combine rotation types for advanced strategies (e.g., time-based with health checks)',
        'Ensure all email accounts are properly warmed up before rotation',
        'Keep detailed logs of rotation patterns for analysis',
        'Test different rotation strategies to find what works best',
        'Adjust rotation intervals based on your sending volume and email account limits'
      ],
      warnings: [
        'Don\'t rotate too frequently - maintain consistency to build reputation',
        'Monitor for email account-specific issues that may require manual intervention',
        'Ensure all email accounts have good reputation before adding to rotation',
        'Avoid sudden changes in rotation patterns - gradual adjustments are better',
        'Volume-Based rotation requires accurate volume tracking',
        'Health-Based rotation needs reliable health metrics - ensure monitoring is accurate',
        'Time-Based rotation may not be suitable for all time zones - consider your audience'
      ],
      examples: [
        {
          title: 'Small Business - Round Robin',
          description: 'A small business with 3 email accounts uses Round Robin for equal distribution',
          code: 'Schedule: Round Robin\nEmail Accounts: support@company.com, sales@company.com, info@company.com\nRotation: Cycles through email accounts sequentially, equal usage'
        },
        {
          title: 'High Volume Campaign - Time-Based',
          description: 'A marketing team sends campaigns throughout the day using time-based rotation',
          code: 'Schedule: Time-Based (every 2 hours)\nEmail Accounts: marketing@company.com, newsletter@company.com, updates@company.com\nRotation: Automatically switches email accounts every 2 hours during business hours'
        },
        {
          title: 'Large Campaign - Volume-Based',
          description: 'A company sending 10,000 emails uses volume-based rotation to avoid rate limits',
          code: 'Schedule: Volume-Based (500 emails per email account)\nEmail Accounts: campaign1@company.com, campaign2@company.com, campaign3@company.com\nRotation: Switches to next email account after 500 emails sent'
        },
        {
          title: 'Reputation Protection - Health-Based',
          description: 'A company automatically switches email accounts when bounce rates exceed thresholds',
          code: 'Schedule: Health-Based (bounce rate > 5%)\nEmail Accounts: primary@company.com, backup@company.com, reserve@company.com\nRotation: Switches to healthy email account when health metrics degrade'
        }
      ]
    },
    'domains': {
      title: 'Domain Management',
      description: 'Manage your email domains and DNS settings for optimal deliverability.',
      strategies: [
        {
          title: 'Domain Verification',
          description: 'Verify domains for sending with your outbound provider',
          benefits: ['Enables sending', 'Builds reputation', 'Compliance requirement'],
          example: 'Add TXT record to domain DNS'
        },
        {
          title: 'Domain Purpose (Source of Truth)',
          description: 'Sets the domain’s intended use and prevents unsafe routing defaults. Today it enforces routing guardrails; mailbox password behavior is documented here so it’s never surprising.',
          benefits: [
            'Prevents business domains from cold outreach',
            'Makes routing rules explicit',
            'Clarifies mailbox credential expectations for tools like Instantly/iOS Mail'
          ],
          example:
            'Cold Outreach domains can route cold_outreach only; Transactional + Newsletter cannot route cold_outreach.'
        },
        {
          title: 'Mailbox Policy (explained)',
          description:
            'How Domain Purpose maps to infrastructure + mailbox policy. Cold Outreach is isolated for deliverability risk; Transactional + Newsletter use Mailgun (for now).',
          benefits: ['Avoids surprises', 'Clear provider expectations', 'Explains Transactional vs Newsletter vs Cold Outreach'],
          example:
            'Transactional → Mailgun · Newsletter → Mailgun (for now) · Cold Outreach → SMTP.com/MailChannels/KG'
        },
        {
          title: 'DNS Configuration',
          description: 'Configure SPF, DKIM, and DMARC records',
          benefits: ['Authentication', 'Deliverability', 'Security'],
          example: 'SPF: v=spf1 include:_spf.smtp.com ~all'
        },
        {
          title: 'Elastic Email outbound setup',
          description: 'Connect Elastic Email for outbound using SMTP relay credentials and DNS auth records.',
          benefits: ['Adds another relay option', 'Reusable relay account across domains', 'No custom API integration required for outbound'],
          example: '1) Relay Accounts: Add Elastic Email relay + save SMTP username/password → 2) Domains → Outbound: select Elastic Email pool → 3) Email Authentication: SPF source Elastic Email + DKIM selector/TXT → Apply + Verify'
        },
        {
          title: 'MX Record Editing',
          description: 'Edit MX records for any domain from any host provider directly from the DNS Setup page',
          benefits: ['Point MX records to any service', 'Works for all host providers', 'Automatic Route 53 updates', 'Cross-account support'],
          example: 'Click DNS button → Edit MX record → Update priority and value → Save'
        }
      ],
      tips: [
        'Always verify domains before sending',
        'Monitor DNS record status regularly',
        'Use subdomains for different campaign types',
        'Keep DNS records up to date',
        'Test DNS configuration with online tools',
        'Elastic Email setup path: Relay Accounts (create Elastic Email relay account) → Domains Outbound (select Elastic Email pool) → Email Authentication (SPF+DKIM+DMARC apply/verify).',
        'Elastic Email in KG uses SMTP username/password for relay auth; no separate custom API integration is required for outbound sending.',
        'Tracking DNS requirement: Type: CNAME | Name: inst (default) | Value: provider target (Instantly.ai: prox.itrackly.com, Apollo.io: track.apollo.io).',
        'Observed DNS value(s) shows what public DNS currently returns for the tracking domain (useful while propagation is pending).',
        'Tracking is DNS-only and does not change upstream relay pools or credentials.',
        'For Route53-managed domains, forwarding setup is one click: KG saves config, updates DNS, and reconciles SSL/edge automatically',
        'Forwarding usually goes live in 10-30 minutes; if SSL is still pending, KG continues to retry in the background',
        'Set Domain Purpose before configuring outbound routing (Cold Outreach vs Transactional vs Newsletter)',
        'Use MX record editing to point domains to different email services',
        'MX records work for domains from any host provider (AWS, etc.)'
      ],
      warnings: [
        'Don\'t send from unverified domains',
        'Monitor DNS record expiration',
        'Ensure proper SPF record configuration',
        'Do not use mailbox IMAP/shared passwords for relay auth. Use provider SMTP relay credentials only.',
        'Avoid using too many subdomains',
        'MX record changes can take 5-60 minutes to propagate',
        'Ensure the MX record value points to a valid mail server',
        'Forwarding can show as pending while SSL cert validation/deployment completes; this is expected and auto-retried',
        'If a domain is not Route53-managed in KG, forwarding DNS must be set manually at your DNS provider',
        'Auto-SPF only applies when KG can confirm the Route 53 Hosted Zone ID (safe guardrail)'
      ],
      examples: [
        {
          title: 'Editing MX Records',
          description: 'How to edit MX records for any domain from any host provider',
          code: '1. Click the "DNS" button on any domain\n2. In the DNS Setup modal, find the "Email Receiving MX Record" section\n3. Click "Edit" next to the MX record\n4. Change the priority (default: 10) and value (e.g., inbound-smtp.us-east-1.amazonaws.com)\n5. Click "Save" to update the MX record in Route 53\n\nWorks for domains from any host provider (AWS, etc.)\n\nThe backend automatically:\n- Finds the Route 53 hosted zone for the domain\n- Updates the MX record with UPSERT (creates if missing, updates if exists)\n- Handles cross-account scenarios if the domain is in a different AWS account'
        }
      ]
    },
    'email-accounts': {
      title: 'Email Account Management',
      description: 'Create and manage email accounts for your campaigns.',
      strategies: [
        {
          title: 'Account Creation',
          description: 'Create accounts with clear naming conventions',
          benefits: ['Organization', 'Easy identification', 'Professional appearance'],
          example: 'admin@domain.com, support@domain.com, noreply@domain.com'
        },
        {
          title: 'Account Warming',
          description: 'Gradually increase sending volume for new accounts',
          benefits: ['Builds reputation', 'Avoids spam flags', 'Better deliverability'],
          example: 'Week 1: 50 emails/day, Week 2: 100 emails/day, Week 3: 200 emails/day'
        }
      ],
      tips: [
        'Use descriptive account names',
        'Warm up new accounts gradually',
        'Monitor account performance metrics',
        'Keep accounts active with regular sending',
        'Use different accounts for different purposes'
      ],
      warnings: [
        'Don\'t create too many accounts at once',
        'Monitor bounce rates closely',
        'Ensure all accounts are properly verified',
        'Avoid sending identical content from all accounts'
      ]
    },
    'aws-account-management': {
      title: 'Hosting Management',
      description: 'Manage your AWS account connections and understand the different connection types.',
      strategies: [
        {
          title: 'Add AWS Account (Production)',
          description: 'Connect a real AWS account using Cross-Account Roles. This is the secure method used in production.',
          benefits: [
            'Secure temporary credentials',
            'Minimal permissions required',
            'No direct AWS account access needed',
            'Industry-standard security practice'
          ],
          example: 'Click "Add AWS Account" → Enter Role ARN and External ID → Secure cross-account connection established'
        },
        {
          title: 'Dev Account (Staging Only)',
          description: 'Create a mock/test AWS account connection for development and testing. This is only available in non-production environments.',
          benefits: [
            'Quick testing without real AWS setup',
            'UI/UX development and testing',
            'Feature validation without AWS credentials',
            'Faster iteration during development'
          ],
          example: 'Click "Dev Account" → Create mock account → Test functionality without real AWS connection'
        }
      ],
      tips: [
        'Use "Add AWS Account" for real production connections - this uses Cross-Account Roles',
        'Use "Dev Account" only in staging/development for testing purposes',
        'Dev Account is not available in production - only real Cross-Account Role connections are used there',
        'Cross-Account Roles provide secure, temporary access without storing AWS credentials',
        'Each AWS account connection can have different security levels and permissions',
        'You can connect multiple AWS accounts to manage different clients or projects',
        'Test connections before sending campaigns to ensure proper setup'
      ],
      warnings: [
        'Dev Account is for testing only - it does not create real AWS connections',
        'Production requires real Cross-Account Roles - Dev Account is not available',
        'Always verify Cross-Account Role ARN and External ID before connecting',
        'Ensure proper IAM permissions are configured in the target AWS account',
        'Monitor connection status regularly to ensure accounts remain connected',
        'Delete unused account connections to maintain security and reduce clutter'
      ]
    },
    'email-pipe-management': {
      title: 'Hosting Management',
      description: 'Connect and manage accounts from different email service providers for domain email delivery.',
      strategies: [
        {
          title: 'AWS SES',
          description: 'Connect your AWS account using Cross-Account Roles for secure email sending.',
          benefits: [
            'Secure temporary credentials',
            'Industry-standard security practice',
            'No direct AWS account access needed',
            'Minimal permissions required'
          ],
          example: 'Click "Add AWS Account" → Enter Role ARN and External ID → Secure cross-account connection established'
        },
        {
          title: 'Mailgun',
          description: 'Connect your Mailgun account for reliable email delivery with advanced analytics.',
          benefits: [
            'High deliverability rates',
            'Advanced analytics and tracking',
            'Developer-friendly API',
            'Flexible pricing options'
          ],
          example: 'Click "Connect Mailgun" → Enter API key and domain → Account connected'
        },
        {
          title: 'Mailreach',
          description: 'Connect your Mailreach account for email delivery with built-in deliverability tools.',
          benefits: [
            'Built-in deliverability tools',
            'Warm-up automation',
            'Real-time analytics',
            'Easy domain verification'
          ],
          example: 'Click "Connect Mailreach" → Enter API credentials → Account connected'
        }
      ],
      tips: [
        'Each provider offers different features and pricing - choose based on your needs',
        'You can connect multiple accounts from the same provider for load distribution',
        'Monitor account status regularly to ensure connections remain active',
        'Use the summary table at the top to see all connected accounts at a glance',
        'Test connections after setup to verify proper configuration'
      ],
      warnings: [
        'AWS SES has strict sandbox limitations and may deny production access',
        'Ensure all credentials are kept secure and never shared',
        'Monitor account usage to avoid exceeding rate limits',
        'Keep API keys and credentials updated to prevent connection failures'
      ]
    },
    'send-pools': {
      title: 'Relay Accounts, Send Pools & Routing',
      description: 'Manage relay accounts, send pools (health/endpoints), outbound routing, and temporary route freezes. No redeploys required.',
      strategies: [
        {
          title: 'Elastic Email quick start',
          description: 'Elastic Email works through SMTP relay credentials (no custom Elastic API integration required). Create one relay account, then assign it in domain outbound routing.',
          benefits: [
            'Fast setup with SMTP username + password',
            'Reusable relay account across multiple domains',
            'Works with existing pool/routing controls'
          ],
          example: 'Relay Accounts → Add Elastic Email relay account → save SMTP username/password → Domains → Outbound → select Elastic Email pool → Apply SPF/DKIM in Email Authentication.'
        },
        {
          title: 'What “Freeze a primary route” does',
          description: 'You pick a domain and a message class (e.g. cold_outreach, transactional, newsletter), set a freezeUntil date (ISO), and click Set. Until that date, the system will not use the primary route for that domain + message class. Leave freezeUntil blank and click Set to clear (unfreeze) that route.',
          benefits: [
            'Pause sending for one domain + message type without touching other traffic',
            'Live control — no code or redeploy',
            'Clear or extend the freeze anytime'
          ],
          example: 'Domain: example.com, Message class: cold_outreach, freezeUntil: 2026-01-01T00:00:00Z → cold outreach for example.com is frozen until that date.'
        },
        {
          title: 'When to use it',
          description: 'Use freezes to protect reputation, respond to incidents, or pause a campaign.',
          benefits: [
            'Reputation / deliverability: pause cold outreach (or other class) when bounces or complaints spike',
            'Incidents: stop a specific primary route for a domain + message type without turning off the whole domain',
            'Operational: pause a campaign or message type during changes or maintenance'
          ],
          example: 'High bounces on cold_outreach for domain.com → set freezeUntil a week ahead; fix issues; clear freeze when ready.'
        },
        {
          title: 'Domain dropdown & Active freezes',
          description: 'Domain is a dropdown of all your domains to avoid typos. Active freezes are listed below the form; use Unfreeze to clear a freeze earlier than the set date.',
          benefits: [
            'No typos in domain names',
            'See all current freezes at a glance',
            'One-click unfreeze when you decide to resume'
          ],
          example: 'Select domain from dropdown → set message class and date → Set. To resume early, click Unfreeze next to that row.'
        },
        {
          title: 'One detail',
          description: 'The freeze applies to that message class on that domain (cold_outreach, transactional, or newsletter). Other message classes on the same domain are unaffected. So if you only freeze cold_outreach, transactional and newsletter for that domain can still send. That\'s so you can pause one type (e.g. cold outreach) without stopping everything.',
          benefits: [
            'Pause only the traffic you need to (e.g. cold outreach)',
            'Transactional and newsletter for the same domain keep sending',
            'Fine-grained control without shutting down the whole domain'
          ]
        }
      ],
      tips: [
        'Elastic Email appears in Relay Accounts after you create an Elastic Email relay account.',
        'Elastic Email uses SMTP credentials (username/password), not a separate custom API connection in KG.',
        'Use the domain dropdown to avoid typos.',
        'freezeUntil must be ISO 8601 (e.g. 2026-01-01T00:00:00Z). Leave blank to clear.',
        'Check the Active freezes list to see what’s currently frozen and use Unfreeze to resume early.',
        'Message class must match how the domain is used (cold_outreach vs transactional vs newsletter).'
      ],
      warnings: [
        'Do not use mailbox IMAP/shared passwords for relay auth. Use provider SMTP relay credentials only.',
        'Freezing only affects the primary route for that domain + message class; failover or other routes may still send if configured.',
        'freezeUntil is in UTC; plan for your time zone.'
      ]
    }
  };

  const content = helpContent[pageKey] || helpContent['cross-identity'];

  // Dynamic sections based on pageKey
  const getSections = () => {
    const baseSections = [
      { key: 'overview', label: 'Overview', icon: InfoCircle }
    ];

    if (pageKey === 'aws-account-management') {
      return [
        ...baseSections,
        { key: 'strategies', label: 'Connection Types', icon: Lightbulb },
        { key: 'tips', label: 'Tips', icon: Lightbulb },
        { key: 'warnings', label: 'Warnings', icon: ExclamationTriangle }
      ];
    } else if (pageKey === 'rotation') {
      return [
        ...baseSections,
        { key: 'strategies', label: 'Rotation Types', icon: Lightbulb },
        { key: 'tips', label: 'Tips', icon: Lightbulb },
        { key: 'warnings', label: 'Warnings', icon: ExclamationTriangle },
        { key: 'examples', label: 'Examples', icon: Book }
      ];
    } else if (pageKey === 'domains') {
      return [
        ...baseSections,
        { key: 'strategies', label: 'Domain Strategies', icon: Lightbulb },
        { key: 'forwarding-setup', label: 'Forwarding', icon: ArrowRight },
        { key: 'setup-receiving', label: 'Setup receiving', icon: CheckCircle },
        { key: 'mx-records', label: 'MX Records', icon: Envelope },
        { key: 'tips', label: 'Tips', icon: Lightbulb },
        { key: 'warnings', label: 'Warnings', icon: ExclamationTriangle },
        { key: 'examples', label: 'Examples', icon: Book }
      ];
    } else if (pageKey === 'email-accounts' || pageKey === 'email-accounts-verify') {
      return [
        ...baseSections,
        { key: 'strategies', label: 'Account Strategies', icon: Lightbulb },
        { key: 'tips', label: 'Tips', icon: Lightbulb },
        { key: 'warnings', label: 'Warnings', icon: ExclamationTriangle }
      ];
    } else if (pageKey === 'email-pipe-management') {
      // Dynamic sections based on selected email pipe
      const pipeSections = [];
      if (selectedEmailPipe === 'ses') {
        pipeSections.push({ key: 'aws-ses', label: 'AWS SES', icon: Cloud });
      } else if (selectedEmailPipe === 'mailgun') {
        pipeSections.push({ key: 'mailgun', label: 'Mailgun', icon: Cloud });
      } else if (selectedEmailPipe === 'mailreach') {
        pipeSections.push({ key: 'mailreach', label: 'Mailreach', icon: Cloud });
      }
      
      return [
        ...baseSections,
        ...pipeSections,
        { key: 'strategies', label: 'All Providers', icon: Lightbulb },
        { key: 'tips', label: 'Tips', icon: Lightbulb },
        { key: 'warnings', label: 'Warnings', icon: ExclamationTriangle }
      ];
    } else if (pageKey === 'send-pools') {
      return [
        ...baseSections,
        { key: 'strategies', label: 'Freeze & routing', icon: Lightbulb },
        { key: 'elastic-email-setup', label: 'Elastic setup', icon: Envelope },
        { key: 'tips', label: 'Tips', icon: Lightbulb },
        { key: 'warnings', label: 'Warnings', icon: ExclamationTriangle }
      ];
    } else {
      // Default: cross-identity
      return [
        ...baseSections,
        { key: 'strategies', label: 'Cross-Identity Strategies', icon: Lightbulb },
        { key: 'benefits', label: 'Cross-Identity Benefits', icon: CheckCircle },
        { key: 'warnings', label: 'Warnings', icon: ExclamationTriangle },
        { key: 'examples', label: 'Examples', icon: Book }
      ];
    }
  };

  const sections = getSections();

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div>
            <h6 className="text-primary mb-3">{content.title}</h6>
            <p className="text-muted">{content.description}</p>
            
            {/* Only show Cross-Identity diagrams for cross-identity page */}
            {pageKey === 'cross-identity' && (
              <div className="mt-4">
                <h6 className="text-secondary mb-3">🎯 How Cross-Identity Works:</h6>
                
                {/* Traditional Single Identity */}
                <div className="mb-3">
                  <div className="visual-diagram">
                    <div className="diagram-header">
                      <strong>Traditional Single Identity:</strong>
                      <Button variant="outline-secondary" size="sm" className="copy-btn">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                        </svg>
                      </Button>
                    </div>
                    <div className="diagram-content">
                      <div className="mb-1">Campaign: "Black Friday Sale"</div>
                      <div className="mb-1">├── From: marketing@krossglobalmarketing.com</div>
                      <div className="mb-1">├── Sends: 10,000 emails</div>
                      <div className="mb-1">└── Risk: High spam flag risk, rate limits</div>
                    </div>
                  </div>
                </div>

                {/* Cross-Identity Distribution */}
                <div className="mb-3">
                  <div className="visual-diagram">
                    <div className="diagram-header">
                      <strong>Cross-Identity Distribution:</strong>
                      <Button variant="outline-secondary" size="sm" className="copy-btn">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                        </svg>
                      </Button>
                    </div>
                    <div className="diagram-content">
                      <div className="mb-1">Campaign: "Black Friday Sale"</div>
                      <div className="mb-1">├── Identity 1: marketing@krossglobalmarketing.com (2,500 emails)</div>
                      <div className="mb-1">├── Identity 2: sales@krossglobal.com (2,500 emails)</div>
                      <div className="mb-1">├── Identity 3: hello@clienta.com (2,500 emails)</div>
                      <div className="mb-1">└── Identity 4: contact@clientb.com (2,500 emails)</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'strategies':
        // Render strategies based on pageKey
        if (pageKey === 'aws-account-management' || pageKey === 'rotation' || pageKey === 'domains' || pageKey === 'email-accounts' || pageKey === 'email-accounts-verify' || pageKey === 'send-pools') {
          // Render strategies from content.strategies array
          return (
            <div>
              <h6 className="text-primary mb-3">
                {pageKey === 'aws-account-management' ? '📊 Connection Types:' : 
                 pageKey === 'rotation' ? '📊 Rotation Types:' :
                 pageKey === 'domains' ? '📊 Domain Strategies:' :
                 pageKey === 'send-pools' ? '📊 Freeze & routing:' :
                 '📊 Account Strategies:'}
              </h6>
              {content.strategies?.map((strategy, index) => (
                <Card key={index} className="mb-3 border-0 bg-light">
                  <Card.Body className="p-3">
                    <h6 className="text-secondary mb-2">{strategy.title}</h6>
                    <p className="text-muted small mb-2">{strategy.description}</p>
                    {strategy.benefits && strategy.benefits.length > 0 && (
                      <div className="mt-2">
                        <strong className="small">Benefits:</strong>
                        <ul className="list-unstyled mt-1 ms-3">
                          {strategy.benefits.map((benefit, idx) => (
                            <li key={idx} className="small text-muted">• {benefit}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {strategy.example && (
                      <div className="mt-2">
                        <Badge bg="dark" className="me-1">Example</Badge>
                        <code className="small text-muted">{strategy.example}</code>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ))}
            </div>
          );
        }
        
        // Default: Cross-Identity strategies with visual diagrams
        return (
          <div>
            <h6 className="text-primary mb-3">📊 Cross-Identity Strategies:</h6>
            
            {/* Strategy 1: Domain Diversification */}
            <div className="mb-3">
              <div className="visual-diagram">
                <div className="diagram-header">
                  <strong>1. Domain Diversification</strong>
                  <Button variant="outline-secondary" size="sm" className="copy-btn">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                    </svg>
                  </Button>
                </div>
                <div className="diagram-content">
                  <div className="mb-1">Campaign: "Newsletter"</div>
                  <div className="mb-1">├── primary@krossglobalmarketing.com (40%)</div>
                  <div className="mb-1">├── newsletter@krossglobal.com (30%)</div>
                  <div className="mb-1">├── updates@kross-marketing.com (20%)</div>
                  <div className="mb-1">└── news@krossglobalmarketing.com (10%)</div>
                </div>
              </div>
            </div>

            {/* Strategy 2: Account Diversification */}
            <div className="mb-3">
              <div className="visual-diagram">
                <div className="diagram-header">
                  <strong>2. Account Diversification</strong>
                  <Button variant="outline-secondary" size="sm" className="copy-btn">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                    </svg>
                  </Button>
                </div>
                <div className="diagram-content">
                  <div className="mb-1">Campaign: "Product Launch"</div>
                  <div className="mb-1">├── admin@krossglobalmarketing.com (25%)</div>
                  <div className="mb-1">├── support@krossglobalmarketing.com (25%)</div>
                  <div className="mb-1">├── hello@clienta.com (25%)</div>
                  <div className="mb-1">└── contact@clientb.com (25%)</div>
                </div>
              </div>
            </div>

            {/* Strategy 3: Geographic Distribution */}
            <div className="mb-3">
              <div className="visual-diagram">
                <div className="diagram-header">
                  <strong>3. Geographic Distribution</strong>
                  <Button variant="outline-secondary" size="sm" className="copy-btn">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                    </svg>
                  </Button>
                </div>
                <div className="diagram-content">
                  <div className="mb-1">Campaign: "Regional Promotion"</div>
                  <div className="mb-1">├── us@krossglobalmarketing.com (US subscribers)</div>
                  <div className="mb-1">├── eu@krossglobal.com (EU subscribers)</div>
                  <div className="mb-1">└── asia@kross-marketing.com (Asia subscribers)</div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'setup-receiving':
        if (pageKey !== 'domains') {
          return null;
        }
        return (
          <div>
            <h6 className="text-primary mb-3">
              <CheckCircle className="me-2" size={18} />
              Setup receiving
            </h6>
            <Card className="border-success mb-3">
              <Card.Header className="bg-success text-white">
                <strong>Setup receiving button</strong>
              </Card.Header>
              <Card.Body>
                <p className="mb-3">
                  Each domain row has a <strong>&quot;Setup receiving&quot;</strong> button. Use it to set or repair inbound email for that domain.
                </p>
                <h6 className="mb-2">What it does:</h6>
                <ul className="mb-3">
                  <li>Inbound email is handled by Postmark (automatic once MX is set)</li>
                  <li>Sets MX to Postmark so messages to this domain are delivered and appear in Unibox</li>
                  <li>Safe to run again if you need to repair receiving or re-apply MX</li>
                </ul>
                <Alert variant="success" className="mb-0">
                  <strong>Safe to run multiple times.</strong> Setup is idempotent: clicking again only ensures configuration is in place; it does not duplicate data or break existing receiving.
                </Alert>
              </Card.Body>
            </Card>
          </div>
        );

      case 'forwarding-setup':
        if (pageKey !== 'domains') {
          return null;
        }
        return (
          <div>
            <h6 className="text-primary mb-3">
              <ArrowRight className="me-2" size={18} />
              Forwarding setup
            </h6>
            <Card className="border-primary mb-3">
              <Card.Header className="bg-primary text-white">
                <strong>One-click forwarding (Route53-managed domains)</strong>
              </Card.Header>
              <Card.Body>
                <p className="mb-3">
                  For domains managed in Route53 through KG, users should only need to click <strong>Set up forwarding</strong>. KG handles
                  DNS + SSL + edge reconciliation automatically.
                </p>
                <h6 className="mb-2">What KG does automatically:</h6>
                <ol className="mb-3">
                  <li>Save forwarding destination and settings</li>
                  <li>Upsert Route53 apex + www forwarding records</li>
                  <li>Create/update SSL certificate validation records</li>
                  <li>Attach aliases/certificate on the redirect edge once issued</li>
                </ol>
                <Alert variant="info" className="mb-3">
                  <strong>User-facing statuses:</strong>
                  <div className="mt-1">DNS updated → Securing domain (SSL) → Forwarding live</div>
                </Alert>
                <h6 className="mb-2">Expected timing:</h6>
                <ul className="mb-0">
                  <li>Typical: <strong>10-30 minutes</strong></li>
                  <li>Sometimes: <strong>up to 60 minutes</strong></li>
                  <li>KG retries edge reconciliation in the background automatically</li>
                </ul>
              </Card.Body>
            </Card>
            <Alert variant="warning" className="mb-0">
              If a domain is not Route53-managed in KG, users must set forwarding DNS manually at their DNS provider.
            </Alert>
          </div>
        );

      case 'mx-records':
        // Only show MX Records section for domains page
        if (pageKey !== 'domains') {
          return null;
        }
        return (
          <div>
            <h6 className="text-primary mb-3">
              <Envelope className="me-2" size={18} />
              MX Record Editing
            </h6>
            <Card className="border-primary mb-3">
              <Card.Header className="bg-primary text-white">
                <strong>How to Edit MX Records for Any Domain</strong>
              </Card.Header>
              <Card.Body>
                <ol className="mb-3">
                  <li className="mb-2">
                    <strong>Click the "DNS" button</strong> on any domain in the table
                  </li>
                  <li className="mb-2">
                    <strong>In the DNS Setup modal</strong>, find the "Email Receiving MX Record" section
                  </li>
                  <li className="mb-2">
                    <strong>Click "Edit"</strong> next to the MX record
                  </li>
                  <li className="mb-2">
                    <strong>Change the priority</strong> (default: 10) and <strong>value</strong> (e.g., <code>inbound-smtp.us-east-1.amazonaws.com</code>)
                  </li>
                  <li className="mb-2">
                    <strong>Click "Save"</strong> to update the MX record in Route 53
                  </li>
                </ol>
                
                <Alert variant="info" className="mb-3">
                  <strong>✅ Works for All Host Providers:</strong> This feature works for domains from any host provider (AWS SES, Mailgun, Mailreach, etc.)
                </Alert>

                <h6 className="mb-2">What Happens Automatically:</h6>
                <ul className="mb-0">
                  <li>The backend <strong>finds the Route 53 hosted zone</strong> for the domain</li>
                  <li>Updates the MX record with <strong>UPSERT</strong> (creates if missing, updates if exists)</li>
                  <li><strong>Handles cross-account scenarios</strong> if the domain is in a different AWS account</li>
                  <li>MX record changes typically take <strong>5-60 minutes</strong> to propagate</li>
                </ul>
              </Card.Body>
            </Card>
          </div>
        );

      case 'tips':
        return (
          <div>
            <h6 className="text-primary mb-3">💡 Tips & Best Practices</h6>
            <ListGroup variant="flush">
              {content.tips?.map((tip, index) => (
                <ListGroup.Item key={index} className="border-0 px-0 py-2">
                  <div className="d-flex">
                    <Lightbulb className="text-info me-2 mt-1" size={16} />
                    <small className="text-muted">{tip}</small>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        );

      case 'elastic-email-setup':
        if (pageKey !== 'send-pools') {
          return null;
        }
        return (
          <div>
            <h6 className="text-primary mb-3">
              <Envelope className="me-2" size={18} />
              Elastic Email setup
            </h6>
            <Card className="border-primary mb-3">
              <Card.Header className="bg-primary text-white">
                <strong>Step-by-step (recommended order)</strong>
              </Card.Header>
              <Card.Body>
                <ol className="mb-3">
                  <li className="mb-2">
                    In <strong>Elastic Email</strong>, authenticate your sending domain and copy:
                    <ul className="mt-1 mb-0">
                      <li>SMTP username</li>
                      <li>SMTP password</li>
                      <li>DKIM selector + DKIM TXT value (selector is often <code>api</code>)</li>
                    </ul>
                  </li>
                  <li className="mb-2">
                    In KG <strong>Relay Accounts</strong>, click <strong>Add relay account</strong>, set provider to <strong>Elastic Email</strong>, then save SMTP username/password.
                  </li>
                  <li className="mb-2">
                    In KG <strong>Domains → Outbound</strong>, select:
                    <ul className="mt-1 mb-0">
                      <li>Outbound Provider: <strong>Elastic Email</strong></li>
                      <li>Primary Send Pool: your Elastic Email pool</li>
                    </ul>
                  </li>
                  <li className="mb-2">
                    Open <strong>Email Authentication (SPF / DKIM / DMARC)</strong> for the domain:
                    <ul className="mt-1 mb-0">
                      <li>SPF source: <code>Elastic Email</code> (<code>include:_spf.elasticemail.com</code>)</li>
                      <li>DKIM: paste selector + TXT value from Elastic Email</li>
                      <li>Apply, then click Verify</li>
                    </ul>
                  </li>
                  <li className="mb-0">
                    Send a test from Unibox and confirm no auth errors.
                  </li>
                </ol>
                <Alert variant="info" className="mb-0">
                  <strong>Note:</strong> KG uses SMTP relay credentials for Elastic Email. No separate custom Elastic API integration is required for outbound sending.
                </Alert>
              </Card.Body>
            </Card>
          </div>
        );

      case 'aws-ses':
        if (pageKey !== 'email-pipe-management' || selectedEmailPipe !== 'ses') {
          return null;
        }
        return (
          <div>
            <h6 className="text-primary mb-3">
              <Cloud className="me-2" size={18} />
              How to Add AWS SES Account
            </h6>
            <Card className="border-warning mb-3">
              <Card.Header className="bg-warning text-dark">
                <strong>AWS SES Setup</strong>
              </Card.Header>
              <Card.Body>
                <p className="mb-3">
                  Click the <strong>"Add AWS Account"</strong> button above, then enter your AWS account's Role ARN and External ID. This creates a secure cross-account connection.
                </p>
                <h6 className="mb-2">Steps:</h6>
                <ol className="mb-0">
                  <li>Click the <strong>"Add AWS Account"</strong> button</li>
                  <li>Enter your AWS Account ID</li>
                  <li>Enter a descriptive Alias for the account</li>
                  <li>Enter the Role ARN from your AWS account</li>
                  <li>Enter the External ID (if configured)</li>
                  <li>Click "Connect" to establish the secure connection</li>
                </ol>
              </Card.Body>
            </Card>
          </div>
        );

      case 'mailgun':
        if (pageKey !== 'email-pipe-management' || selectedEmailPipe !== 'mailgun') {
          return null;
        }
        return (
          <div>
            <h6 className="text-primary mb-3">
              <Cloud className="me-2" size={18} />
              How to Add Mailgun Account
            </h6>
            <Card className="border-info mb-3">
              <Card.Header className="bg-info text-white">
                <strong>Mailgun Setup</strong>
              </Card.Header>
              <Card.Body>
                <p className="mb-3">
                  Click the <strong>"Connect Mailgun"</strong> button above to connect your Mailgun account. This will guide you through the setup process.
                </p>
                <h6 className="mb-2">Steps:</h6>
                <ol className="mb-0">
                  <li>Click the <strong>"Connect Mailgun"</strong> button</li>
                  <li>Enter your Mailgun API key</li>
                  <li>Enter your Mailgun domain</li>
                  <li>Follow the automated setup process</li>
                  <li>Verify domain ownership</li>
                </ol>
              </Card.Body>
            </Card>
          </div>
        );

      case 'mailreach':
        if (pageKey !== 'email-pipe-management' || selectedEmailPipe !== 'mailreach') {
          return null;
        }
        return (
          <div>
            <h6 className="text-primary mb-3">
              <Cloud className="me-2" size={18} />
              How to Add Mailreach Account
            </h6>
            <Card className="border-success mb-3">
              <Card.Header className="bg-success text-white">
                <strong>Mailreach Setup</strong>
              </Card.Header>
              <Card.Body>
                <p className="mb-3">
                  Click the <strong>"Connect Mailreach"</strong> button above to connect your Mailreach account. This will guide you through the setup process.
                </p>
                <h6 className="mb-2">Steps:</h6>
                <ol className="mb-0">
                  <li>Click the <strong>"Connect Mailreach"</strong> button</li>
                  <li>Enter your Mailreach API credentials</li>
                  <li>Enter your domain information</li>
                  <li>Follow the automated setup process</li>
                  <li>Complete domain verification</li>
                </ol>
              </Card.Body>
            </Card>
          </div>
        );

      case 'benefits':
        // Only show benefits for cross-identity page
        if (pageKey !== 'cross-identity') {
          return null;
        }
        return (
          <div>
            <h6 className="text-primary mb-3">✅ Cross-Identity Benefits:</h6>
            
            {/* Benefits Visual Diagram */}
            <div className="mb-3">
              <div className="bg-light p-3 rounded border">
                <div className="mb-3">
                  <strong>1. Deliverability Protection</strong>
                  <ul className="list-unstyled mt-2 ms-3">
                    <li className="mb-1">• Reduces spam flags by distributing sends</li>
                    <li className="mb-1">• Maintains sender reputation across identities</li>
                    <li className="mb-1">• Avoids single-point-of-failure issues</li>
                  </ul>
                </div>
                
                <div className="mb-3">
                  <strong>2. Rate Limit Management</strong>
                  <ul className="list-unstyled mt-2 ms-3">
                    <li className="mb-1">• Spreads load across multiple identities</li>
                    <li className="mb-1">• Increases total sending capacity</li>
                    <li className="mb-1">• Prevents hitting individual limits</li>
                  </ul>
                </div>
                
                <div className="mb-3">
                  <strong>3. Performance Optimization</strong>
                  <ul className="list-unstyled mt-2 ms-3">
                    <li className="mb-1">• A/B testing across different identities</li>
                    <li className="mb-1">• Performance comparison between domains</li>
                    <li className="mb-1">• Optimization based on results</li>
                  </ul>
                </div>
                
                <div className="mb-0">
                  <strong>4. Risk Mitigation</strong>
                  <ul className="list-unstyled mt-2 ms-3">
                    <li className="mb-1">• If one identity gets flagged, others continue</li>
                    <li className="mb-1">• Diversified sending patterns</li>
                    <li className="mb-1">• Reduced dependency on single identity</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Cross-Identity vs Rotation Comparison */}
            <div className="mb-3">
              <h6 className="text-secondary mb-3">🔄 Cross-Identity vs Rotation:</h6>
              
              {/* Rotation (Sequential) */}
              <div className="mb-3">
                <div className="visual-diagram">
                  <div className="diagram-header">
                    <strong>Rotation (Sequential):</strong>
                    <Button variant="outline-secondary" size="sm" className="copy-btn">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                      </svg>
                    </Button>
                  </div>
                  <div className="diagram-content">
                    <div className="mb-1">Time-based switching:</div>
                    <div className="mb-1">├── Hour 1: admin@domain.com</div>
                    <div className="mb-1">├── Hour 2: support@domain.com</div>
                    <div className="mb-1">└── Hour 3: noreply@domain.com</div>
                  </div>
                </div>
              </div>

              {/* Cross-Identity (Parallel) */}
              <div className="mb-3">
                <div className="visual-diagram">
                  <div className="diagram-header">
                    <strong>Cross-Identity (Parallel):</strong>
                    <Button variant="outline-secondary" size="sm" className="copy-btn">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                      </svg>
                    </Button>
                  </div>
                  <div className="diagram-content">
                    <div className="mb-1">Simultaneous distribution:</div>
                    <div className="mb-1">├── admin@domain.com: 33% of campaign</div>
                    <div className="mb-1">├── support@domain.com: 33% of campaign</div>
                    <div className="mb-1">└── noreply@domain.com: 34% of campaign</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'warnings':
        return (
          <div>
            <h6 className="text-primary mb-3">Important Warnings</h6>
            <ListGroup variant="flush">
              {content.warnings?.map((warning, index) => (
                <ListGroup.Item key={index} className="border-0 px-0 py-2">
                  <div className="d-flex">
                    <ExclamationTriangle className="text-warning me-2 mt-1" size={16} />
                    <small className="text-muted">{warning}</small>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        );

      case 'examples':
        return (
          <div>
            <h6 className="text-primary mb-3">Real-World Examples</h6>
            {content.examples?.map((example, index) => (
              <Card key={index} className="mb-3 border-0 bg-light">
                <Card.Body className="p-3">
                  <h6 className="text-secondary mb-2">{example.title}</h6>
                  <p className="text-muted small mb-2">{example.description}</p>
                  {example.code && (
                    <div className="mt-2">
                      <Badge bg="dark" className="me-1">Implementation</Badge>
                      <code className="small text-muted">{example.code}</code>
                    </div>
                  )}
                </Card.Body>
              </Card>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Help Sidebar */}
      <Offcanvas
        show={isOpen}
        onHide={onToggle}
        placement="end"
        style={{ width: '350px' }}
        className="help-sidebar"
      >
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="d-flex align-items-center">
            <QuestionCircle className="me-2 text-primary" size={20} />
            <span className="fw-bold">Help & Education</span>
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body className="p-0">
          {/* Navigation Tabs */}
          <div className="border-bottom">
            <div className="nav nav-pills nav-fill">
              {sections.map((section) => {
                const IconComponent = section.icon;
                return (
                  <button
                    key={section.key}
                    className={`nav-link py-2 px-2 ${activeSection === section.key ? 'active' : ''}`}
                    onClick={() => setActiveSection(section.key)}
                    style={{ fontSize: '12px' }}
                  >
                    <IconComponent size={14} className="d-block mx-auto mb-1" />
                    <span>{section.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="p-3" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            {renderContent()}
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default HelpSidebar;
